import type { NeurogresSkill } from "./skillGameMap";

export type BlockPatternCategory =
  | "disqualifier"
  | "stopping_question"
  | "ego_victim"
  | "meta_model";

export interface BlockPattern {
  key: string;
  category: BlockPatternCategory;
  label: string;
  pattern: RegExp;
  skill?: NeurogresSkill;
}

export const DISQUALIFIER_PATTERNS: readonly BlockPattern[] = [
  {
    key: "nothing_changes",
    category: "disqualifier",
    label: "Nothing changes",
    pattern: /\b(nothing (ever )?(changes|works)|this (won't|will not) work|what('?s| is) the point)\b/i,
    skill: "FlexibleThinking",
  },
  {
    key: "identity_lock",
    category: "disqualifier",
    label: "Identity lock",
    pattern: /\b(this is just who i am|that('?s| is) just how i am|i('?m| am) just like this)\b/i,
    skill: "FlexibleThinking",
  },
  {
    key: "impossibility",
    category: "disqualifier",
    label: "Impossibility framing",
    pattern: /\b(i can'?t|i cannot|it('?s| is) impossible|there('?s| is) no way)\b/i,
    skill: "TakingInitiative",
  },
  {
    key: "all_or_nothing",
    category: "disqualifier",
    label: "All-or-nothing framing",
    pattern: /\b(always|never|everyone|no one|nothing|everything)\b/i,
    skill: "FlexibleThinking",
  },
];

export const STOPPING_QUESTION_PATTERNS: readonly BlockPattern[] = [
  {
    key: "what_if_fail",
    category: "stopping_question",
    label: "Failure forecasting",
    pattern: /\bwhat if (i|it) (fail|fails|doesn'?t work|does not work)\b/i,
    skill: "CopingWithChanges",
  },
  {
    key: "why_bother",
    category: "stopping_question",
    label: "Why bother",
    pattern: /\b(why bother|what('?s| is) the point)\b/i,
    skill: "TakingInitiative",
  },
  {
    key: "how_know",
    category: "stopping_question",
    label: "Certainty challenge",
    pattern: /\b(how do you know|who says|says who)\b/i,
    skill: "AnalysisAndDeduction",
  },
  {
    key: "why_me",
    category: "stopping_question",
    label: "Why me",
    pattern: /\bwhy me\b/i,
    skill: "FlexibleThinking",
  },
];

export const EGO_VICTIM_PATTERNS: readonly BlockPattern[] = [
  {
    key: "external_control",
    category: "ego_victim",
    label: "External control",
    pattern: /\b(they|he|she|it) (make|makes|made) me\b/i,
    skill: "TakingInitiative",
  },
  {
    key: "powerlessness",
    category: "ego_victim",
    label: "Powerlessness",
    pattern: /\b(i have no choice|i don'?t have a choice|there('?s| is) nothing i can do)\b/i,
    skill: "DevelopingAPlan",
  },
  {
    key: "self_attack",
    category: "ego_victim",
    label: "Self-attack",
    pattern: /\b(i('?m| am) broken|i('?m| am) a failure|i('?m| am) not enough)\b/i,
    skill: "TakingInitiative",
  },
  {
    key: "helpless_cycle",
    category: "ego_victim",
    label: "Helpless cycle",
    pattern: /\bthis always happens to me|life always happens to me|i always get stuck\b/i,
    skill: "Planning",
  },
];

export const META_MODEL_PATTERNS: readonly BlockPattern[] = [
  {
    key: "modal_necessity",
    category: "meta_model",
    label: "Modal necessity",
    pattern: /\b(have to|need to|must|should|supposed to)\b/i,
    skill: "Planning",
  },
  {
    key: "modal_impossibility",
    category: "meta_model",
    label: "Modal impossibility",
    pattern: /\b(can'?t|cannot|unable to)\b/i,
    skill: "TakingInitiative",
  },
  {
    key: "unspecified_other",
    category: "meta_model",
    label: "Unspecified other",
    pattern: /\b(they|people|everyone) (think|say|know|expect)\b/i,
    skill: "AnalysisAndDeduction",
  },
  {
    key: "cause_effect",
    category: "meta_model",
    label: "Cause-effect",
    pattern: /\b(x|it|this|they) (makes|made) me\b|\bbecause of (them|this|that)\b/i,
    skill: "FlexibleThinking",
  },
];

export const ALL_BLOCK_PATTERNS: readonly BlockPattern[] = [
  ...DISQUALIFIER_PATTERNS,
  ...STOPPING_QUESTION_PATTERNS,
  ...EGO_VICTIM_PATTERNS,
  ...META_MODEL_PATTERNS,
];
